import React, { useState, useEffect } from 'react';
import './Exchange.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// The Archivist character image
const ARCHIVIST_IMAGE = "https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/ux6uqi2x_The%20Archivist.png";

function Exchange({ user, token, arcoBalance, onClose }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [basket, setBasket] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategorySettings, setShowCategorySettings] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Product form state
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    files: []  // Support multiple files for bundles
  });

  const isFounder = user?.role === 'Founder';

  // Load data on mount
  useEffect(() => {
    loadCategories();
    loadProducts();
    loadOrders();
    checkPaymentStatus();
  }, [token]);

  // Check if returning from payment
  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('exchange_success');
    
    if (success && sessionId) {
      // Poll for payment status
      try {
        const response = await fetch(`${API_URL}/api/exchange/checkout/status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            setSuccessMessage('Thank you for your contribution! Check your email for download links.');
            setBasket([]);
            loadOrders();
          }
        }
      } catch (err) {
        console.error('Error checking payment:', err);
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
        if (data.categories?.length > 0 && !selectedCategory) {
          setSelectedCategory(data.categories[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadProducts = async (category = null) => {
    setLoading(true);
    try {
      const url = category 
        ? `${API_URL}/api/exchange/products?category=${category}`
        : `${API_URL}/api/exchange/products`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  // Category selection
  const selectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    loadProducts(categoryId);
  };

  // Basket functions
  const addToBasket = (product) => {
    if (!basket.find(p => p.id === product.id)) {
      setBasket([...basket, product]);
    }
  };

  const removeFromBasket = (productId) => {
    setBasket(basket.filter(p => p.id !== productId));
  };

  const basketTotal = basket.reduce((sum, p) => sum + p.price, 0);

  // Checkout
  const handleCheckout = async () => {
    if (basket.length === 0) return;
    
    setCheckoutLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/exchange/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_ids: basket.map(p => p.id),
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        alert(error.detail || 'Checkout failed');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Resend download email
  const resendDownload = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/resend-download/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Download email sent! Check your inbox.');
      } else {
        alert('Failed to send email');
      }
    } catch (err) {
      console.error('Resend error:', err);
    }
  };

  // Admin: Add category
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    const newCat = {
      id: newCategoryName.toLowerCase().replace(/\s+/g, '-'),
      name: newCategoryName,
      description: ''
    };
    
    const updatedCategories = [...categories, newCat];
    
    try {
      const response = await fetch(`${API_URL}/api/exchange/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categories: updatedCategories })
      });
      if (response.ok) {
        setCategories(updatedCategories);
        setNewCategoryName('');
      }
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  // Admin: Delete category
  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category?')) return;
    
    const updatedCategories = categories.filter(c => c.id !== categoryId);
    
    try {
      const response = await fetch(`${API_URL}/api/exchange/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categories: updatedCategories })
      });
      if (response.ok) {
        setCategories(updatedCategories);
        if (selectedCategory === categoryId) {
          setSelectedCategory(updatedCategories[0]?.id || null);
        }
      }
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  // Admin: Add product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('name', newProduct.name);
    formData.append('description', newProduct.description);
    formData.append('price', newProduct.price);
    formData.append('category', newProduct.category || selectedCategory);
    
    // Append all files for bundle support
    if (newProduct.files && newProduct.files.length > 0) {
      for (const file of newProduct.files) {
        formData.append('files', file);
      }
    }
    
    try {
      const response = await fetch(`${API_URL}/api/exchange/products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        setShowProductForm(false);
        setNewProduct({ name: '', description: '', price: '', category: '', files: [] });
        loadProducts(selectedCategory);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add product');
      }
    } catch (err) {
      console.error('Error adding product:', err);
    }
  };

  // Admin: Delete product
  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/exchange/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        loadProducts(selectedCategory);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const filteredProducts = selectedCategory 
    ? products.filter(p => p.category === selectedCategory)
    : products;

  return (
    <div className="exchange-overlay" onClick={onClose}>
      <div className="exchange-modal" onClick={(e) => e.stopPropagation()} data-testid="exchange-modal">

        {/* Header */}
        <div className="exchange-header">
          <h1 className="exchange-title">THE ARCOLIAN EXCHANGE</h1>
          <p className="exchange-subtitle">Where knowledge finds its keeper</p>
          <button className="exchange-close" onClick={onClose}>×</button>
          {isFounder && (
            <button 
              className="exchange-settings-btn"
              onClick={() => setShowCategorySettings(!showCategorySettings)}
              title="Manage Categories"
            >
              ⚙
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="exchange-success">
            {successMessage}
            <button onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}

        {/* Main Content */}
        <div className="exchange-content">
          
          {/* Left Panel - Categories */}
          <div className="exchange-categories">
            <div className="categories-header">COLLECTIONS</div>
            
            {showCategorySettings && isFounder && (
              <div className="category-settings">
                <input
                  type="text"
                  placeholder="New category..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="category-input"
                />
                <button onClick={addCategory} className="category-add-btn">+</button>
              </div>
            )}
            
            {categories.map(cat => (
              <div key={cat.id} className="category-item-wrapper">
                <button
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => selectCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                >
                  <span className="category-name">{cat.name}</span>
                </button>
                {showCategorySettings && isFounder && (
                  <button 
                    className="category-delete-btn"
                    onClick={() => deleteCategory(cat.id)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            
            {/* The Archivist Character - at bottom of categories */}
            <div className="archivist-container">
              <img src={ARCHIVIST_IMAGE} alt="The Archivist" className="archivist-image" />
            </div>
          </div>

          {/* Center Panel - Products */}
          <div className="exchange-products">
            <div className="products-header">
              <span>OFFERINGS</span>
              {isFounder && (
                <button 
                  className="add-product-btn"
                  onClick={() => setShowProductForm(true)}
                >
                  + Add Scroll
                </button>
              )}
            </div>
            
            {/* Add Product Form */}
            {showProductForm && isFounder && (
              <form className="product-form" onSubmit={handleAddProduct}>
                <input
                  type="text"
                  placeholder="Name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  required
                />
                <textarea
                  placeholder="Description"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price (GBP)"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  required
                />
                <select
                  value={newProduct.category || selectedCategory}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <div className="file-upload-section">
                  <label className="file-upload-label">
                    Files (select multiple for bundles)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setNewProduct({...newProduct, files: Array.from(e.target.files)})}
                    className="file-input"
                  />
                  {newProduct.files && newProduct.files.length > 0 && (
                    <div className="selected-files">
                      {newProduct.files.map((file, idx) => (
                        <div key={`${file.name}-${file.lastModified}`} className="selected-file">
                          <span>📄 {file.name}</span>
                          <button 
                            type="button" 
                            onClick={() => {
                              const newFiles = [...newProduct.files];
                              newFiles.splice(idx, 1);
                              setNewProduct({...newProduct, files: newFiles});
                            }}
                            className="remove-file-btn"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-buttons">
                  <button type="submit" className="form-submit">Create Scroll</button>
                  <button type="button" className="form-cancel" onClick={() => setShowProductForm(false)}>Cancel</button>
                </div>
              </form>
            )}
            
            <div className="products-grid">
              {loading ? (
                <div className="products-loading">Loading scrolls...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="products-empty">No scrolls in this collection yet.</div>
              ) : (
                filteredProducts.map(product => (
                  <div key={product.id} className="product-card" data-testid={`product-${product.id}`}>
                    <div className="product-icon">📜</div>
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-desc">{product.description}</p>
                      <div className="product-price">£{product.price.toFixed(2)}</div>
                      {product.file_count > 1 && (
                        <div className="product-bundle-badge">Bundle: {product.file_count} files</div>
                      )}
                    </div>
                    <div className="product-actions">
                      <button 
                        className="save-knowledge-btn"
                        onClick={() => addToBasket(product)}
                        disabled={basket.find(p => p.id === product.id)}
                      >
                        {basket.find(p => p.id === product.id) ? 'Saved' : 'Save Knowledge'}
                      </button>
                      {isFounder && (
                        <button 
                          className="delete-product-btn"
                          onClick={() => deleteProduct(product.id)}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - User & Basket */}
          <div className="exchange-user-panel">
            {/* User Info */}
            <div className="user-info-section">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username}</span>
                <span className={`user-role role-${(user?.role || 'Member').toLowerCase()}`}>
                  {user?.role || 'Member'}
                </span>
              </div>
              <div className="user-balance">
                <span className="balance-label">ARCO Balance</span>
                <span className="balance-value">{arcoBalance?.toLocaleString() || '0'} ◈</span>
              </div>
            </div>

            {/* Purchase History */}
            <div className="history-section">
              <div className="history-header">ARCHIVES OF ACQUISITION</div>
              <div className="history-list">
                {orders.length === 0 ? (
                  <p className="history-empty">No acquisitions yet.</p>
                ) : (
                  orders.map(order => (
                    <div key={order.id} className="history-item">
                      <div className="history-info">
                        <span className="history-items">{order.product_names?.join(', ')}</span>
                        <span className="history-amount">£{order.amount?.toFixed(2)}</span>
                      </div>
                      <button 
                        className="download-again-btn"
                        onClick={() => resendDownload(order.id)}
                      >
                        Download Again
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Basket */}
            <div className="basket-section">
              <div className="basket-header">VESSEL OF KNOWLEDGE</div>
              {basket.length === 0 ? (
                <p className="basket-empty">Your vessel awaits...</p>
              ) : (
                <>
                  <div className="basket-items">
                    {basket.map(item => (
                      <div key={item.id} className="basket-item">
                        <span className="basket-item-name">{item.name}</span>
                        <span className="basket-item-price">£{item.price.toFixed(2)}</span>
                        <button 
                          className="basket-remove"
                          onClick={() => removeFromBasket(item.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="basket-total">
                    <span>Total:</span>
                    <span>£{basketTotal.toFixed(2)}</span>
                  </div>
                  <button 
                    className="acquire-btn"
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Processing...' : 'Acquire Knowledge'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Exchange;
